import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { AUTO_LABEL } from "@/brand";
import { MapPin, Clock, CheckCircle2, Sparkles, Navigation, Radio } from "lucide-react";
import { API } from "@/config";
import { loadGoogleMaps } from "@/lib/googleMapsLoader";

/* ==================== Public: Track Driver ================================
 * Customer-facing tracking link the driver sends when they hit "Send
 * Notification". Renders an interactive Google Map that polls the backend
 * every 30 s. The driver's marker smoothly animates between position
 * updates so it feels like a live dot rather than a jumping pin. Falls
 * back to a Static Maps thumbnail when the JS API can't load (blocked
 * network, missing key, older browser).
 */

// Countdown hook: pass the moment the ETA was computed and the ETA in
// minutes, get back the number of minutes remaining right now.
const useCountdown = (startIso, minutes) => {
  const [remaining, setRemaining] = useState(minutes || 0);
  useEffect(() => {
    if (!startIso || !minutes) return;
    const start = new Date(startIso).getTime();
    const totalMs = minutes * 60 * 1000;
    const tick = () => {
      const elapsed = Date.now() - start;
      const left = Math.max(0, Math.round((totalMs - elapsed) / 60000));
      setRemaining(left);
    };
    tick();
    const id = setInterval(tick, 15_000);
    return () => clearInterval(id);
  }, [startIso, minutes]);
  return remaining;
};

// Simple lat/lng linear interpolation, called on every animation frame to
// slide the driver marker smoothly from its previous position to the new
// one over ~1.5 s. Prevents the pin from "teleporting" every 30 s.
const animateMarker = (marker, from, to, duration = 1500) => {
  if (!marker || !from || !to) return;
  const startTs = performance.now();
  const step = (ts) => {
    const t = Math.min(1, (ts - startTs) / duration);
    // ease-out cubic for a natural deceleration.
    const eased = 1 - Math.pow(1 - t, 3);
    const lat = from.lat + (to.lat - from.lat) * eased;
    const lng = from.lng + (to.lng - from.lng) * eased;
    marker.setPosition({ lat, lng });
    if (t < 1) requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
};

const carIconSvg = (heading = 0) =>
  `data:image/svg+xml;utf8,${encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' width='48' height='48' viewBox='0 0 48 48'>
      <g transform='rotate(${heading || 0} 24 24)'>
        <circle cx='24' cy='24' r='18' fill='rgba(255,209,102,0.25)' />
        <circle cx='24' cy='24' r='11' fill='#FFD166' stroke='#040A0F' stroke-width='2'/>
        <path d='M24 14 L28 22 L24 20 L20 22 Z' fill='#040A0F'/>
      </g>
    </svg>`
  )}`;

const LiveMap = ({ data }) => {
  const mapDivRef = useRef(null);
  const mapRef = useRef(null);
  const destMarkerRef = useRef(null);
  const driverMarkerRef = useRef(null);
  const lastDriverPosRef = useRef(null);
  const [jsMapReady, setJsMapReady] = useState(false);
  const [jsMapError, setJsMapError] = useState(null);

  // Boot the JS Maps API once we have a destination coord to center on.
  useEffect(() => {
    if (!data?.destination_lat || !data?.destination_lng) return;
    let cancelled = false;
    loadGoogleMaps()
      .then((maps) => {
        if (cancelled || !mapDivRef.current) return;
        const center = { lat: data.destination_lat, lng: data.destination_lng };
        const map = new maps.Map(mapDivRef.current, {
          center,
          zoom: 14,
          disableDefaultUI: true,
          gestureHandling: "greedy",
          styles: [
            // Muted dark-navy map tuned to the Enchanted brand.
            { elementType: "geometry", stylers: [{ color: "#0d1e2c" }] },
            { elementType: "labels.text.stroke", stylers: [{ color: "#0d1e2c" }] },
            { elementType: "labels.text.fill", stylers: [{ color: "#8ea6bb" }] },
            { featureType: "water", elementType: "geometry", stylers: [{ color: "#0a172a" }] },
            { featureType: "road", elementType: "geometry", stylers: [{ color: "#1a3251" }] },
            { featureType: "road", elementType: "geometry.stroke", stylers: [{ color: "#122841" }] },
            { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#2a4a6b" }] },
            { featureType: "poi", stylers: [{ visibility: "off" }] },
            { featureType: "transit", stylers: [{ visibility: "off" }] },
            { featureType: "administrative", elementType: "geometry.stroke", stylers: [{ color: "#2a3f52" }] },
          ],
        });
        destMarkerRef.current = new maps.Marker({
          position: center,
          map,
          title: data.destination,
          icon: {
            path: maps.SymbolPath.CIRCLE,
            scale: 10,
            fillColor: "#FFD166",
            fillOpacity: 1,
            strokeColor: "#040A0F",
            strokeWeight: 3,
          },
          label: { text: "E", color: "#040A0F", fontWeight: "700", fontSize: "12px" },
        });
        mapRef.current = map;
        setJsMapReady(true);
      })
      .catch((err) => setJsMapError(err.message));
    return () => { cancelled = true; };
  }, [data?.destination_lat, data?.destination_lng, data?.destination]);

  // Every time `data` refreshes, push the latest driver position onto the
  // map — creating the marker on first sight and smoothly sliding it after.
  useEffect(() => {
    if (!jsMapReady || !mapRef.current) return;
    const maps = window.google?.maps;
    if (!maps) return;
    if (data?.delivered) {
      if (driverMarkerRef.current) {
        driverMarkerRef.current.setMap(null);
        driverMarkerRef.current = null;
      }
      return;
    }
    if (data?.driver_lat == null || data?.driver_lng == null) return;
    const target = { lat: data.driver_lat, lng: data.driver_lng };
    if (!driverMarkerRef.current) {
      driverMarkerRef.current = new maps.Marker({
        position: target,
        map: mapRef.current,
        icon: {
          url: carIconSvg(data.driver_heading),
          anchor: new maps.Point(24, 24),
          scaledSize: new maps.Size(48, 48),
        },
        title: "Your driver",
      });
      lastDriverPosRef.current = target;
      // Fit the map to show both driver + destination on first sighting.
      const bounds = new maps.LatLngBounds();
      bounds.extend(target);
      if (data.destination_lat != null && data.destination_lng != null) {
        bounds.extend({ lat: data.destination_lat, lng: data.destination_lng });
      }
      mapRef.current.fitBounds(bounds, 80);
      return;
    }
    // Smooth animate from last known position → new one.
    const from = lastDriverPosRef.current;
    if (from && (from.lat !== target.lat || from.lng !== target.lng)) {
      animateMarker(driverMarkerRef.current, from, target);
      lastDriverPosRef.current = target;
      // Update heading if provided.
      if (data.driver_heading != null) {
        driverMarkerRef.current.setIcon({
          url: carIconSvg(data.driver_heading),
          anchor: new maps.Point(24, 24),
          scaledSize: new maps.Size(48, 48),
        });
      }
    }
  }, [jsMapReady, data?.driver_lat, data?.driver_lng, data?.driver_heading, data?.delivered, data?.destination_lat, data?.destination_lng]);

  // Fallback state — static map thumbnail if the JS API errored out.
  if (jsMapError && data?.static_map_url) {
    return (
      <div className="rounded-2xl overflow-hidden border border-[#FFD166]/20 bg-black/40" data-testid="track-static-map">
        <img src={data.static_map_url} alt="Delivery destination map" className="w-full h-auto block" />
      </div>
    );
  }
  return (
    <div className="rounded-2xl overflow-hidden border border-[#FFD166]/20 bg-black/40" data-testid="track-live-map">
      <div ref={mapDivRef} className="w-full h-72 sm:h-80" />
    </div>
  );
};

export const TrackDriver = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const timerRef = useRef(null);
  const [pollHeartbeat, setPollHeartbeat] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/track/${token}`);
      setData(data);
      setPollHeartbeat(new Date().toISOString());
      setError("");
    } catch (e) {
      setError(e.response?.data?.detail || "This tracking link is no longer active.");
    }
  }, [token]);

  useEffect(() => {
    load();
    // 30-second poll matches the driver's GPS post cadence — any faster
    // just burns bandwidth without new data to render.
    timerRef.current = setInterval(load, 30_000);
    return () => timerRef.current && clearInterval(timerRef.current);
  }, [load]);

  const eta = useCountdown(data?.eta_computed_at, data?.eta_min);
  const hasLive = data?.driver_lat != null && data?.driver_lng != null;
  const locationAgeSec = data?.location_updated_at
    ? Math.max(0, Math.round((Date.now() - new Date(data.location_updated_at).getTime()) / 1000))
    : null;

  return (
    <div className="min-h-screen text-[#F6F1E4]" style={{ background: "linear-gradient(180deg,#040A0F,#0A2438 45%,#040A0F)" }}>
      <div className="max-w-lg mx-auto px-5 py-10">
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 mb-2">
            <Sparkles className="w-4 h-4 text-[#FFD166]" />
            <span className="text-[10px] uppercase tracking-[0.28em] text-[#FFD166]">Enchanted Gear Rentals</span>
          </div>
          <h1 className="font-serif text-3xl">Track your driver</h1>
        </div>

        {error && (
          <div className="rounded-xl border border-red-500/40 bg-red-500/10 p-6 text-center" data-testid="track-error">
            <div className="text-red-200 mb-2">{error}</div>
            <div className="text-xs text-[#B9C8D5]">If your gear hasn't arrived yet, please call the number on your booking confirmation.</div>
          </div>
        )}

        {data && !error && (
          <div className="space-y-5" data-testid="track-page">
            {data.delivered ? (
              <div className="rounded-2xl border border-emerald-500/40 bg-emerald-500/10 p-6 text-center" data-testid="track-delivered">
                <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-300 mb-2" />
                <div className="font-serif text-2xl text-emerald-100">Delivered!</div>
                <div className="text-sm text-emerald-100/80 mt-1">We hope you have a magical vacation ✨</div>
              </div>
            ) : (
              <div className="rounded-2xl border border-[#FFD166]/30 bg-[#FFD166]/5 p-6 text-center" data-testid="track-eta">
                <Clock className="w-8 h-8 mx-auto text-[#FFD166] mb-2" />
                <div className="text-xs uppercase tracking-widest text-[#B9C8D5]">Arriving in about</div>
                <div className="font-serif text-5xl text-[#FFD166] my-1">{eta}<span className="text-2xl ml-1">min</span></div>
                <div className="text-xs text-[#B9C8D5]">Hi {data.customer_first_name}! Please head to the front of your resort to receive your items.</div>
                {hasLive && (
                  <div className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-200 text-[10px] uppercase tracking-widest" data-testid="track-live-badge">
                    <Radio className="w-3 h-3 animate-pulse" />
                    Live · driver spotted {locationAgeSec != null ? `${locationAgeSec}s ago` : "just now"}
                  </div>
                )}
              </div>
            )}

            <LiveMap data={data} />

            <div className="rounded-xl border border-[#FFD166]/20 bg-black/30 p-4" data-testid="track-destination">
              <div className="text-[10px] uppercase tracking-widest text-[#B9C8D5] mb-1">Destination</div>
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-[#FFD166] mt-0.5 flex-shrink-0" />
                <div className="text-sm">{data.destination}</div>
              </div>
              {data.directions_url && (
                <a
                  href={data.directions_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 mt-3 text-xs text-[#FFD166] hover:text-[#F4A93A]"
                  data-testid="track-open-in-maps"
                >
                  <Navigation className="w-3.5 h-3.5" /> Open in Maps →
                </a>
              )}
            </div>

            <div className="text-center text-[10px] text-[#B9C8D5]" data-testid="track-heartbeat">
              {hasLive ? "Live position updates every 30 seconds" : "Waiting for driver to share location…"}
              {pollHeartbeat && <> · Last check {new Date(pollHeartbeat).toLocaleTimeString()}</>}
            </div>

            <div className="rounded-xl border border-[#FFD166]/15 bg-black/20 p-3 text-center text-[11px] text-[#B9C8D5]" data-testid="track-contact">
              <div>{AUTO_LABEL}</div>
              <div className="text-[10px] mt-0.5">Texts from this number are automated. Reply to reach your driver during delivery.</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrackDriver;
